import { useState, useEffect, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { startOfDay, endOfDay, format } from 'date-fns';
import { formatStartOfDay, formatEndOfDay } from '../utils/timezone-utils';

// Maximum jobs per page
const JOBS_PER_PAGE = 50;

// Define the GET_JOBS query for completed jobs
const GET_JOBS = gql`
  query GetJobs($input: JobsQueryInput!) {
    jobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        hasDateOverride
        hasTimeOverride
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

// Helper function to consistently format dates to YYYY-MM-DD
const formatDateKey = (date) => {
  try {
    let dateObj;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      if (/^\d+$/.test(date)) {
        dateObj = new Date(parseInt(date, 10));
      } else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date; // Already in correct format
      } else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return null;
    }
  
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    // Format as YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return null;
  }
};

// Helper function to check if a job is a clean job
const isCleanJob = (job) => {
  // Check if it's a built-in cleaning service type
  const isBuiltInCleaning = job.serviceType?.title?.toLowerCase().includes('clean') || 
                           job.serviceType?.title?.toLowerCase() === 'cleaning';
  
  // Check if category is CLEANING
  const isCleaningCategory = job.category === 'CLEANING';
  
  // Check if service type category is CLEANING
  const isServiceTypeCleaning = job.serviceType?.category === 'CLEANING';
  
  return isBuiltInCleaning || isCleaningCategory || isServiceTypeCleaning;
};

// Group jobs by date
const groupJobsByDate = (jobs) => {
  const grouped = {};
  
  if (!jobs || !Array.isArray(jobs)) return grouped;
  
  jobs.forEach(job => {
    // Use scheduled date (job date) instead of completion date for completed jobs
    const dateKey = formatDateKey(job.scheduledDate);
    if (!dateKey) return;
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    
    grouped[dateKey].push(job);
  });
  
  // Sort each date's jobs by scheduled time
  Object.keys(grouped).forEach(dateKey => {
    grouped[dateKey].sort((a, b) => {
      const timeA = new Date(a.scheduledDate);
      const timeB = new Date(b.scheduledDate);
      return timeA - timeB; // Chronological order (earliest first)
    });
  });
  
  return grouped;
};

export function useCompletedJobs(user, searchTerm = '', dateRange = { startDate: new Date(), endDate: new Date() }) {
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [accumulatedJobs, setAccumulatedJobs] = useState([]);
  const [loadedPages, setLoadedPages] = useState(new Set([1]));
  const [page, setPage] = useState(1);
  
  // Determine statuses to fetch based on user role and job type
  const getCompletedJobStatuses = (userRole) => {
    // For all roles, include both COMPLETED and READY_FOR_INSPECTION
    // The filtering by job type will be done on the client side
    return ['COMPLETED', 'READY_FOR_INSPECTION'];
  };
  
  // Use the selected date range directly since we're now filtering by scheduled date
  const formattedStartDate = useMemo(() => {
    return formatStartOfDay(startOfDay(dateRange.startDate));
  }, [dateRange.startDate]);
  
  const formattedEndDate = useMemo(() => {
    return formatEndOfDay(endOfDay(dateRange.endDate));
  }, [dateRange.endDate]);
  
  // Fetch completed jobs with date filtering
  const { 
    loading: completedJobsLoading, 
    data: completedJobsData,
    refetch: refetchCompletedJobs,
    fetchMore: fetchMoreCompleted,
    error: completedJobsError
  } = useQuery(GET_JOBS, {
    variables: {
      input: {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        assignedToUserId: user?.id ? parseInt(user.id, 10) : undefined,
        statuses: getCompletedJobStatuses(user?.role),
        search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
        limit: JOBS_PER_PAGE,
        offset: 0
      }
    },
    skip: !user?.id,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
    onCompleted: (data) => {
      if (data?.jobs?.items) {
        // Filter jobs by valid statuses and job types
        const validStatuses = getCompletedJobStatuses(user?.role);
        const completedJobs = data.jobs.items.filter(job => {
          // CRITICAL SECURITY FIX: Ensure user can only see their own jobs
          if (!job.assignedTo || job.assignedTo.id !== user?.id) {
            return false;
          }
          
          // Check if job has a valid status
          if (!validStatuses.includes(job.status)) {
            return false;
          }
          
          // For clean jobs, show both COMPLETED and READY_FOR_INSPECTION
          // For non-clean jobs, only show COMPLETED
          if (isCleanJob(job)) {
            return job.status === 'COMPLETED' || job.status === 'READY_FOR_INSPECTION';
          } else {
            return job.status === 'COMPLETED';
          }
        });
        setAccumulatedJobs(completedJobs);
        setLoadedPages(new Set([1]));
        setPage(1);
      }
      
      setHasMore(data?.jobs?.hasMore || false);
      setIsLoadingMore(false);
    },
    onError: (error) => {
      setIsLoadingMore(false);
      console.error('Error fetching completed jobs:', error);
    }
  });

  // Process and group completed jobs by scheduled date
  const completedJobs = useMemo(() => {
    const jobs = accumulatedJobs.length > 0 ? accumulatedJobs : (completedJobsData?.jobs?.items || []);
    
    // Filter jobs by the selected date range based on scheduled date
    const jobsInDateRange = jobs.filter(job => {
      // CRITICAL SECURITY FIX: Ensure user can only see their own jobs
      if (!job.assignedTo || job.assignedTo.id !== user?.id) {
        return false;
      }
      
      // Check if job has a valid status
      const validStatuses = getCompletedJobStatuses(user?.role);
      if (!validStatuses.includes(job.status)) return false;
      
      // For clean jobs, show both COMPLETED and READY_FOR_INSPECTION
      // For non-clean jobs, only show COMPLETED
      if (isCleanJob(job)) {
        if (job.status !== 'COMPLETED' && job.status !== 'READY_FOR_INSPECTION') {
          return false;
        }
      } else {
        if (job.status !== 'COMPLETED') {
          return false;
        }
      }
      
      const scheduledDate = new Date(job.scheduledDate);
      const startDate = startOfDay(dateRange.startDate);
      const endDate = endOfDay(dateRange.endDate);
      
      const isInRange = scheduledDate >= startDate && scheduledDate <= endDate;
      
      return isInRange;
    });
    
    return groupJobsByDate(jobsInDateRange);
  }, [completedJobsData, accumulatedJobs, dateRange, completedJobsError, user?.role, getCompletedJobStatuses]);

  // Calculate total count
  const totalCount = useMemo(() => {
    return Object.values(completedJobs).flat().length;
  }, [completedJobs]);

  // Function to load more jobs
  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const nextPage = page + 1;
      const result = await fetchMoreCompleted({
        variables: {
          input: {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            assignedToUserId: user?.id ? parseInt(user.id, 10) : undefined,
            statuses: getCompletedJobStatuses(user?.role),
            search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
            limit: JOBS_PER_PAGE,
            offset: (nextPage - 1) * JOBS_PER_PAGE
          }
        }
      });
      
      if (result.data?.jobs?.items) {
        // Filter jobs by valid statuses and job types
        const validStatuses = getCompletedJobStatuses(user?.role);
        const completedJobs = result.data.jobs.items.filter(job => {
          // CRITICAL SECURITY FIX: Ensure user can only see their own jobs
          if (!job.assignedTo || job.assignedTo.id !== user?.id) {
            return false;
          }
          
          // Check if job has a valid status
          if (!validStatuses.includes(job.status)) {
            return false;
          }
          
          // For clean jobs, show both COMPLETED and READY_FOR_INSPECTION
          // For non-clean jobs, only show COMPLETED
          if (isCleanJob(job)) {
            return job.status === 'COMPLETED' || job.status === 'READY_FOR_INSPECTION';
          } else {
            return job.status === 'COMPLETED';
          }
        });
        setAccumulatedJobs(prev => [...prev, ...completedJobs]);
        setLoadedPages(prev => {
          const newSet = new Set(prev);
          newSet.add(nextPage);
          return newSet;
        });
        setPage(nextPage);
        setHasMore(result.data.jobs.hasMore);
      }
    } catch (error) {
      console.error('Error loading more completed jobs:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Function to refetch all data
  const refetchAllCompletedJobs = () => {
    // Reset accumulated jobs and pages
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));
    setPage(1);
    
    refetchCompletedJobs();
  };

  // Reset accumulated jobs when search term or date range changes
  useEffect(() => {
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));
    setPage(1);
    setHasMore(true);
  }, [searchTerm, user?.id, formattedStartDate, formattedEndDate]);

  return {
    completedJobs,
    completedJobsLoading,
    isLoadingMore,
    hasMore,
    handleLoadMore,
    refetchCompletedJobs: refetchAllCompletedJobs,
    totalCount,
    error: completedJobsError
  };
} 